import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/radio?tab=designer).
 *
 * @slot default - Slot para o rótulo do rádio, como alternativa à propriedade label.
 */

@Component({
  tag: 'br-radio',
  styleUrl: 'radio.scss',
  shadow: true,
  formAssociated: true,
})
export class Radio {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrRadioElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Define o estado de seleção do radio.
   * Se definido como verdadeiro, o radio estará marcado. Caso contrário, estará desmarcado.
   */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false

  /**
   * Desativa o radio, tornando-o não interativo.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Indica a validade do radio.
   * Se não for especificado, o valor padrão é `null`, indicando que a validade não foi definida.
   */
  @Prop({ reflect: true }) readonly state: 'valid' | 'invalid'

  /**
   * Define se o label associado ao radio deve ser oculto.
   * Se definido como verdadeiro, o texto do label será oculto, mas o radio ainda estará visível e funcional.
   */
  @Prop({ reflect: true, attribute: 'has-hidden-label' }) readonly hasHiddenLabel: boolean = false

  /**
   * Identificador único; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('br-radio')

  /**
   * Define o nome do radio, que é utilizado para agrupar radios em formulários e identificar o campo.
   * O valor é obrigatório e deve ser fornecido para garantir o correto funcionamento em formulários.
   */

  @Prop({ reflect: true }) readonly name!: string

  /**
   * Texto descritivo exibido à direita do radio.
   * Caso um slot seja utilizado para fornecer um texto alternativo, o valor desta propriedade será ignorado.
   */
  @Prop({ reflect: true }) readonly label: string

  /**
   * Define o valor associado ao radio quando ele faz parte de um formulário nativo (`<form>`).
   * Esse valor é enviado com o formulário quando o radio está selecionado.
   * **Nota:** Esta propriedade não deve ser utilizada para determinar se o radio está selecionado; para verificar o estado de seleção, use a propriedade `checked`.
   */
  @Prop({ reflect: true }) readonly value: string

  @State() updateTrigger: number = 0

  /**
   * Disparado depois que o valor do `checked` foi alterado
   */
  @Event({ bubbles: true, composed: true }) checkedChange: EventEmitter<boolean>

  @Watch('checked')
  checkedChanged(isChecked: boolean) {
    this.checkedChange.emit(isChecked)
  }

  @Listen('checkedChange', { target: 'window' })
  handleCheckedChange(event: CustomEvent) {
    if (
      (event.target as HTMLBrRadioElement).customId !== this.customId &&
      (event.target as HTMLBrRadioElement).name === this.name
    ) {
      const isSiblingChecked = event.detail

      if (isSiblingChecked) {
        if (this.checked) {
          this.checked = false
        }
      }
      // Always re-evaluate focusability (tabIndex) when siblings change
      this.updateTrigger++
    }
  }

  /**
   * Navega e seleciona o próximo/anterior rádio habilitado no grupo via teclas de seta.
   * Ativado por 'keydown', move a seleção entre rádios do mesmo 'name'
   * usando as setas, partindo do rádio atualmente checado.
   * @param {KeyboardEvent} event O evento de teclado.
   * @listens keydown
   */
  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (this.disabled) return

    const isArrowKey = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)
    if (!isArrowKey) return

    // Prevent default scrolling
    event.preventDefault()

    const root = this.el.getRootNode() as Document | ShadowRoot
    const radios = Array.from(root.querySelectorAll<HTMLBrRadioElement>('br-radio')).filter(
      (el: HTMLBrRadioElement) => el.name === this.name && !el.disabled
    )

    const currentIndex = radios.indexOf(this.el)
    if (currentIndex === -1) return

    let nextIndex = currentIndex
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % radios.length
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + radios.length) % radios.length
    }

    const nextRadio = radios[nextIndex]
    if (nextRadio) {
      if (!nextRadio.checked) {
        nextRadio.toggleChecked()
      }
      ;(nextRadio as any).setFocus()
    }
  }

  /**
   * Inverte o valor da prop `checked`
   */
  @Method()
  async toggleChecked() {
    this.checked = !this.checked
  }

  @Method()
  async setFocus() {
    const input = this.el.shadowRoot?.querySelector('input')
    if (input) {
      input.focus()
    }
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-radio': true,
      valid: this.state === 'valid',
      invalid: this.state === 'invalid',
      disabled: this.disabled,
      'hidden-label': this.hasHiddenLabel,
    }
  }

  /**
   * Handler para o event `click`
   * @param event Objeto Event
   */
  private readonly onClick = (event: MouseEvent) => {
    event.stopPropagation()
    this.checked = (event.target as HTMLInputElement).checked
  }

  /**
   * Handler para o evento `change`
   */
  private readonly onChange = () => {
    // this.checkedChange.emit(this.checked) - handled by Watch
  }

  private computeTabIndex(): number {
    if (this.disabled) return -1
    if (this.checked) return 0

    // If unchecked, we are only focusable (0) if NO sibling is checked AND we are the first enabled sibling.
    const root = this.el.getRootNode() as Document | ShadowRoot
    if (!root) return 0 // Fallback

    const radios = Array.from(root.querySelectorAll<HTMLBrRadioElement>('br-radio')).filter(
      (r) => r.name === this.name && !r.disabled
    )

    const anyChecked = radios.some((r) => r.checked)
    if (anyChecked) return -1

    return radios[0] === this.el ? 0 : -1
  }

  render() {
    return (
      <Host aria-disabled={this.disabled ? 'true' : null}>
        <div class={this.getCssClassMap()}>
          <input
            part="radio-input"
            aria-label={this.hasHiddenLabel && this.label ? this.label : null}
            type="radio"
            id={this.customId}
            name={this.name}
            checked={this.checked}
            disabled={this.disabled}
            value={this.value}
            tabIndex={this.computeTabIndex()}
            onClick={this.onClick}
            onChange={this.onChange}
          />
          <label part="radio-label" htmlFor={this.customId}>
            <slot>{this.label}</slot>
          </label>
        </div>
      </Host>
    )
  }
}
